import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, interval, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/** Items per page in the bell panel — use pagination, not a single low cap. */
export const NOTIFICATIONS_PAGE_SIZE = 50;

export type NotificationServiceType =
  | 'labdip'
  | 'bulk'
  | 'sourcing'
  | 'trim'
  | 'print-embroidery'
  | 'proto';

export interface AppNotificationItem {
  id: number;
  kind: string;
  service_type: NotificationServiceType;
  entity_id: number;
  title: string;
  body?: string | null;
  created_at: string;
  read_at?: string | null;
  /** Optional context — when available, the bell shows “Brand · Actor”. */
  brand_name?: string | null;
  actor_name?: string | null;
  actor_role?: 'client' | 'staff' | null;
}

export interface AppNotificationsListResponse {
  /** Total unread for this user (not limited by page size). */
  unread_count: number;
  items: AppNotificationItem[];
  next_cursor?: string | null;
}

/** Consent copy version stored when the user opts in (must match backend doc). */
export const WHATSAPP_OPT_IN_VERSION = '2026-07-21';

export interface NotificationPreferences {
  phone_e164: string | null;
  whatsapp_opt_in: boolean;
  whatsapp_opt_in_at?: string | null;
  whatsapp_opt_in_version?: string | null;
  whatsapp_reminder_enabled: boolean;
}

export interface UpdateNotificationPreferencesDTO {
  phone_e164?: string | null;
  whatsapp_opt_in?: boolean;
  whatsapp_reminder_enabled?: boolean;
  whatsapp_opt_in_version?: string;
}

export interface ReminderBatchItem {
  notification_id: number;
  kind: string;
  service_type: NotificationServiceType;
  entity_id: number;
  title: string;
  body?: string | null;
  brand_name?: string | null;
  actor_name?: string | null;
  created_at: string;
  seen_at?: string | null;
  dismissed?: boolean;
}

export interface ReminderBatchResponse {
  id: string;
  created_at: string;
  item_count: number;
  items: ReminderBatchItem[];
}

export interface OpenReminderBatchItemResponse {
  notification_id: number;
  seen_at: string;
  service_type: NotificationServiceType;
  entity_id: number;
}

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly baseUrl = `${environment.url}/api/notifications`;
  private readonly mePrefsUrl = `${environment.url}/api/notifications/preferences`;
  private readonly state$ = new BehaviorSubject<AppNotificationsListResponse>({
    unread_count: 0,
    items: [],
    next_cursor: null,
  });
  private readonly prefsSubject = new BehaviorSubject<NotificationPreferences | null>(null);
  private pollSub: Subscription | null = null;
  private scheduledRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  readonly notifications$ = this.state$.asObservable();
  /** Cached WhatsApp prefs for sidebar status (null until first load). */
  readonly preferences$ = this.prefsSubject.asObservable();

  constructor(private http: HttpClient) {}

  get preferencesSnapshot(): NotificationPreferences | null {
    return this.prefsSubject.value;
  }

  getNotificationPreferences(): Observable<NotificationPreferences> {
    return this.http.get<NotificationPreferences>(this.mePrefsUrl).pipe(
      tap((prefs) => this.prefsSubject.next(prefs))
    );
  }

  updateNotificationPreferences(
    payload: UpdateNotificationPreferencesDTO
  ): Observable<NotificationPreferences> {
    return this.http.post<NotificationPreferences>(this.mePrefsUrl, payload).pipe(
      tap((prefs) => this.prefsSubject.next(prefs))
    );
  }

  getReminderBatch(batchId: string): Observable<ReminderBatchResponse> {
    return this.http.get<ReminderBatchResponse>(
      `${this.baseUrl}/reminder-batches/${encodeURIComponent(batchId)}`
    );
  }

  openReminderBatchItem(
    batchId: string,
    notificationId: number
  ): Observable<OpenReminderBatchItemResponse> {
    return this.http.post<OpenReminderBatchItemResponse>(
      `${this.baseUrl}/reminder-batches/${encodeURIComponent(batchId)}/items/${notificationId}/open`,
      {}
    );
  }

  /**
   * Coalesces multiple refreshes triggered in quick succession (e.g. when an
   * action fires several HTTP requests). Refreshes once ~600ms later.
   *
   * Use this from the {@link NotificationsRefreshInterceptor} so that any
   * write to a service entity (comment, approve, reject, ...) makes the bell
   * react immediately instead of waiting for the 60s poll.
   */
  scheduleRefresh(delayMs = 600): void {
    if (this.scheduledRefreshTimer) return;
    this.scheduledRefreshTimer = setTimeout(() => {
      this.scheduledRefreshTimer = null;
      this.refresh().subscribe();
    }, delayMs);
  }

  get snapshot(): AppNotificationsListResponse {
    return this.state$.value;
  }

  get hasMore(): boolean {
    return !!this.state$.value.next_cursor;
  }

  startPolling(intervalMs = 60_000): void {
    this.stopPolling();
    this.refresh().subscribe();
    this.pollSub = interval(intervalMs)
      .pipe(switchMap(() => this.refresh()))
      .subscribe();
  }

  stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  /** First page — used on poll and when opening the bell. */
  refresh(): Observable<AppNotificationsListResponse> {
    return this.fetchPage().pipe(tap((res) => this.state$.next(res)));
  }

  /** Append next page when the user scrolls / clicks “Load more”. */
  loadMore(): Observable<AppNotificationsListResponse> {
    const cursor = this.state$.value.next_cursor;
    if (!cursor) return of(this.state$.value);

    return this.fetchPage(cursor).pipe(
      tap((res) => {
        const current = this.state$.value;
        this.state$.next({
          unread_count: res.unread_count,
          items: [...current.items, ...(res.items || [])],
          next_cursor: res.next_cursor ?? null,
        });
      })
    );
  }

  /**
   * Dismiss a single notification for the **current user only**.
   * Does NOT remove it for other recipients of the same group.
   */
  dismissOneForCurrentUser(id: number): Observable<void> {
    const current = this.state$.value;
    if (current.items.some((n) => n.id === id)) {
      this.state$.next({
        unread_count: Math.max(0, current.unread_count - 1),
        items: current.items.filter((n) => n.id !== id),
        next_cursor: current.next_cursor,
      });
    }
    return this.http.post<void>(`${this.baseUrl}/${id}/dismiss`, {}).pipe(catchError(() => of(void 0)));
  }

  private fetchPage(cursor?: string): Observable<AppNotificationsListResponse> {
    let params = new HttpParams()
      .set('limit', String(NOTIFICATIONS_PAGE_SIZE))
      .set('unread_only', 'true');

    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return this.http.get<AppNotificationsListResponse>(this.baseUrl, { params }).pipe(
      catchError(() => of({ unread_count: 0, items: [], next_cursor: null })),
      map((res) => this.normalizeUnreadFeed(res))
    );
  }

  private normalizeUnreadFeed(res: AppNotificationsListResponse): AppNotificationsListResponse {
    const items = (res.items || []).filter((n) => !n.read_at);
    return {
      unread_count: Number(res.unread_count ?? items.length),
      items,
      next_cursor: res.next_cursor ?? null,
    };
  }
}
